import React, { useEffect, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Badge,
    Nav,
    NavItem,
    NavLink,
    Table,
    Spinner,
    Alert,
    Input,
    FormGroup,
    Label
} from "reactstrap";
import classnames from "classnames";
import { get, post } from "helpers/api_helper";
import moment from "moment";
import AuditTrailModal from "./AuditTrailModal";

const AdminApprovalsModal = ({ isOpen, toggle, onStatusChange }) => {
    const [activeTab, setActiveTab] = useState("pending");
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [actionLoadingId, setActionLoadingId] = useState(null);

    // Reject reason prompt state
    const [rejectingId, setRejectingId] = useState(null);
    const [rejectionReason, setRejectionReason] = useState("");

    // Audit trail viewer state
    const [selectedAuditDoc, setSelectedAuditDoc] = useState(null);

    const fetchRequests = async (status = activeTab) => {
        setLoading(true);
        setError(null);
        try {
            const res = await get(`/api/approvals/list?status=${status === "all" ? "" : status}&limit=50`);
            if (res.status === 200) {
                setRequests(res.response?.rows || []);
            } else {
                setError(res.message || "Failed to fetch approval requests.");
            }
        } catch (err) {
            setError(err?.message || "Failed to fetch approvals.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchRequests(activeTab);
        }
    }, [isOpen, activeTab]);

    const handleAction = async (id, status, reason = null) => {
        setActionLoadingId(id);
        setError(null);
        try {
            const payload = {
                id,
                status,
                rejection_reason: reason
            };
            const res = await post("/api/approvals/respond", payload);
            if (res.status === 200) {
                setRejectingId(null);
                setRejectionReason("");
                fetchRequests(activeTab);
                if (onStatusChange) onStatusChange();
            } else {
                setError(res.message || "Action failed.");
            }
        } catch (err) {
            setError(err?.message || "Action failed.");
        } finally {
            setActionLoadingId(null);
        }
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case "approved":
                return (
                    <span
                        style={{
                            background: "#dcfce7",
                            color: "#166534",
                            border: "1px solid #86efac",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "700",
                            display: "inline-flex",
                            alignItems: "center"
                        }}
                    >
                        <i className="bx bx-check-circle me-1"></i> Approved
                    </span>
                );
            case "rejected":
                return (
                    <span
                        style={{
                            background: "#fee2e2",
                            color: "#991b1b",
                            border: "1px solid #fca5a5",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "700",
                            display: "inline-flex",
                            alignItems: "center"
                        }}
                    >
                        <i className="bx bx-x-circle me-1"></i> Rejected
                    </span>
                );
            case "used":
                return (
                    <span
                        style={{
                            background: "#e0f2fe",
                            color: "#0369a1",
                            border: "1px solid #bae6fd",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "700",
                            display: "inline-flex",
                            alignItems: "center"
                        }}
                    >
                        <i className="bx bx-check-double me-1"></i> Edit Completed
                    </span>
                );
            case "pending":
            default:
                return (
                    <span
                        style={{
                            background: "#fef3c7",
                            color: "#92400e",
                            border: "1px solid #fcd34d",
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "700",
                            display: "inline-flex",
                            alignItems: "center"
                        }}
                    >
                        <i className="bx bx-time-five me-1"></i> Pending Review
                    </span>
                );
        }
    };

    return (
        <>
            <Modal isOpen={isOpen} toggle={toggle} size="xl" centered scrollable zIndex="99999">
                <ModalHeader toggle={toggle} className="d-flex justify-content-between align-items-center">
                    <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "16px" }}>
                        <i className="mdi mdi-shield-check text-primary me-2"></i>
                        Order & Invoice Edit Approval Center
                    </div>
                </ModalHeader>

                <ModalBody>
                    <Nav tabs className="mb-3">
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === "pending" })}
                                onClick={() => setActiveTab("pending")}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: activeTab === "pending" ? "700" : "500",
                                    color: activeTab === "pending" ? "#b45309" : "#475569"
                                }}
                            >
                                <i className="mdi mdi-clock-outline me-1 text-warning"></i> Pending Requests
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === "approved" })}
                                onClick={() => setActiveTab("approved")}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: activeTab === "approved" ? "700" : "500",
                                    color: activeTab === "approved" ? "#15803d" : "#475569"
                                }}
                            >
                                <i className="mdi mdi-check-circle-outline me-1 text-success"></i> Approved
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === "used" })}
                                onClick={() => setActiveTab("used")}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: activeTab === "used" ? "700" : "500",
                                    color: activeTab === "used" ? "#0369a1" : "#475569"
                                }}
                            >
                                <i className="mdi mdi-history me-1 text-info"></i> Completed Edits
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === "rejected" })}
                                onClick={() => setActiveTab("rejected")}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: activeTab === "rejected" ? "700" : "500",
                                    color: activeTab === "rejected" ? "#b91c1c" : "#475569"
                                }}
                            >
                                <i className="mdi mdi-close-circle-outline me-1 text-danger"></i> Rejected
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === "all" })}
                                onClick={() => setActiveTab("all")}
                                style={{
                                    cursor: "pointer",
                                    fontWeight: activeTab === "all" ? "700" : "500",
                                    color: activeTab === "all" ? "#0f172a" : "#475569"
                                }}
                            >
                                All Requests
                            </NavLink>
                        </NavItem>
                    </Nav>

                    {error && <Alert color="danger" className="py-2">{error}</Alert>}

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner color="primary" />
                            <div className="mt-2 text-muted">Loading requests...</div>
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="text-center py-5 text-muted">
                            <i className="mdi mdi-clipboard-text-outline f-24 d-block mb-2 text-secondary" style={{ fontSize: "40px" }}></i>
                            No {activeTab !== "all" ? activeTab : ""} approval requests found.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table className="align-middle table-nowrap table-hover mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>Doc Type</th>
                                        <th>Doc #</th>
                                        <th>Requested By</th>
                                        <th>Requested Date</th>
                                        <th style={{ width: "30%" }}>Reason</th>
                                        <th>Status</th>
                                        <th className="text-end">Actions / Audit</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.map((item) => (
                                        <tr key={item.id}>
                                            <td>
                                                <span
                                                    style={{
                                                        background: item.document_type === "order" ? "#e0e7ff" : "#f1f5f9",
                                                        color: item.document_type === "order" ? "#3730a3" : "#334155",
                                                        border: `1px solid ${item.document_type === "order" ? "#c7d2fe" : "#cbd5e1"}`,
                                                        padding: "3px 8px",
                                                        borderRadius: "6px",
                                                        fontSize: "11px",
                                                        fontWeight: "700",
                                                        letterSpacing: "0.5px"
                                                    }}
                                                >
                                                    {item.document_type?.toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                <strong style={{ color: "#0f172a", fontSize: "13px" }}>#{item.document_number || item.document_id}</strong>
                                            </td>
                                            <td>
                                                <div style={{ color: "#0f172a", fontWeight: "700", fontSize: "13px" }}>{item.requested_by_name || "Data Entry User"}</div>
                                                <span style={{ color: "#64748b", fontSize: "11px", fontWeight: "500" }}>ID: {item.requested_by}</span>
                                            </td>
                                            <td>
                                                <span style={{ color: "#334155", fontWeight: "600", fontSize: "12px" }}>
                                                    {moment(item.requested_at).format("DD MMM YYYY, hh:mm A")}
                                                </span>
                                            </td>
                                            <td>
                                                <div style={{ maxWidth: "280px", whiteSpace: "normal", fontSize: "13px", color: "#0f172a", fontWeight: "600" }}>
                                                    {item.reason}
                                                </div>
                                                {item.rejection_reason && (
                                                    <div style={{ color: "#b91c1c", fontSize: "12px", marginTop: "4px", fontWeight: "600" }}>
                                                        <strong>Rejection Note:</strong> {item.rejection_reason}
                                                    </div>
                                                )}
                                                {item.approved_by_name && (
                                                    <div style={{ color: "#15803d", fontSize: "12px", marginTop: "4px", fontWeight: "600" }}>
                                                        Reviewed by: {item.approved_by_name} ({moment(item.approved_at).format("DD MMM, hh:mm A")})
                                                    </div>
                                                )}
                                            </td>
                                            <td>{getStatusBadge(item.status)}</td>
                                            <td className="text-end" style={{ whiteSpace: "nowrap" }}>
                                                {item.status === "pending" ? (
                                                    <div className="btn-group btn-group-sm">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm"
                                                            style={{
                                                                background: "#10b981",
                                                                color: "#ffffff",
                                                                border: "none",
                                                                fontWeight: "700",
                                                                padding: "5px 12px",
                                                                borderRadius: "6px 0 0 6px"
                                                            }}
                                                            disabled={actionLoadingId === item.id}
                                                            onClick={() => handleAction(item.id, "approved")}
                                                        >
                                                            {actionLoadingId === item.id ? <Spinner size="sm" /> : <><i className="bx bx-check me-1"></i> Approve</>}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm"
                                                            style={{
                                                                background: "#ef4444",
                                                                color: "#ffffff",
                                                                border: "none",
                                                                fontWeight: "700",
                                                                padding: "5px 12px",
                                                                borderRadius: "0 6px 6px 0"
                                                            }}
                                                            disabled={actionLoadingId === item.id}
                                                            onClick={() => setRejectingId(item.id)}
                                                        >
                                                            <i className="bx bx-x me-1"></i> Reject
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm"
                                                        style={{
                                                            background: "#e0f2fe",
                                                            color: "#0369a1",
                                                            border: "1px solid #bae6fd",
                                                            fontWeight: "600",
                                                            padding: "5px 12px",
                                                            borderRadius: "6px",
                                                            fontSize: "12px",
                                                            display: "inline-flex",
                                                            alignItems: "center"
                                                        }}
                                                        onClick={() => setSelectedAuditDoc({
                                                            document_type: item.document_type,
                                                            document_id: item.document_id,
                                                            document_number: item.document_number
                                                        })}
                                                    >
                                                        <i className="bx bx-history me-1 font-size-14 align-middle"></i> Audit Log
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </ModalBody>

                <ModalFooter>
                    <Button color="secondary" onClick={toggle}>Close</Button>
                    <Button color="primary" onClick={() => fetchRequests(activeTab)}>
                        <i className="mdi mdi-refresh me-1"></i> Refresh
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Reject Reason Confirmation Modal */}
            <Modal isOpen={Boolean(rejectingId)} toggle={() => setRejectingId(null)} centered size="sm">
                <ModalHeader toggle={() => setRejectingId(null)}>Reject Edit Request</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="rejectReasonText">Rejection Reason (Optional)</Label>
                        <Input
                            type="textarea"
                            id="rejectReasonText"
                            rows="2"
                            placeholder="Reason for declining edit..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                        />
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={() => setRejectingId(null)}>Cancel</Button>
                    <Button
                        color="danger"
                        size="sm"
                        disabled={actionLoadingId === rejectingId}
                        onClick={() => handleAction(rejectingId, "rejected", rejectionReason)}
                    >
                        {actionLoadingId === rejectingId ? <Spinner size="sm" /> : "Confirm Reject"}
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Audit Trail Modal */}
            {selectedAuditDoc && (
                <AuditTrailModal
                    isOpen={Boolean(selectedAuditDoc)}
                    toggle={() => setSelectedAuditDoc(null)}
                    documentType={selectedAuditDoc.document_type}
                    documentId={selectedAuditDoc.document_id}
                    documentNumber={selectedAuditDoc.document_number}
                />
            )}
        </>
    );
};

export default AdminApprovalsModal;
